import { UNEXPECTED_ERROR } from '../constants/services';
import fetch from '../utils/fetch';
import CustomError from '../utils/CustomError';
import { installedProductsV4 } from '../constants/endpoints';

export async function getInstalledProduct(token, id, filters) {
  try {
    const query = await fetch(`${installedProductsV4}/${id}`, {
      method: 'get',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: `Bearer ${token}`
      },
      params: { ...filters }
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      data: json.data,
      info: json.info,
      meta: json._meta
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function getInstalledProductsLocations(token, type) {
  try {
    const query = await fetch(`${installedProductsV4}/locations`, {
      method: 'get',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: `Bearer ${token}`
      },
      params: { type }
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      data: json.data,
      info: json.info
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function getInstalledProducts(token, topFills, category) {
  try {
    const res = await Promise.all(
      topFills.map(topFill => {
        const result = getInstalledProduct(token, topFill.id, category);
        return result;
      })
    );
    const response = {
      data: res
    };
    return response;
  } catch (error) {
    if (error.isCustomError) {
      throw error;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function getHistoricalAlarm({
  token,
  id,
  metric = null,
  startDate,
  endDate,
  applyDateFormat = true
}) {
  try {
    const url = `${installedProductsV4}/${id}/history/logs`;
    const query = await fetch(url, {
      method: 'get',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: `Bearer ${token}`
      },
      params: { metric, startDate, endDate, applyDateFormat }
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      data: json.data,
      info: json.info
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function getHistoricalAlarmDownload({
  token,
  id,
  metric = null,
  startDate,
  endDate,
  format,
  applyDateFormat = true
}) {
  try {
    const query = await fetch(
      `${installedProductsV4}/${id}/history/logs/download`,
      {
        method: 'get',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          Authorization: `Bearer ${token}`
        },
        params: { metric, startDate, endDate, applyDateFormat, format }
      }
    );
    const transactionId = query.headers.get('transactionId');
    const response = await query.blob();
    if (!query.ok) {
      throw new CustomError({ ...UNEXPECTED_ERROR, transactionId });
    }
    return {
      data: response
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function getHistoryInstalledProduct(token, id, params) {
  try {
    const query = await fetch(`${installedProductsV4}/${id}/history/series`, {
      method: 'get',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: `Bearer ${token}`
      },
      params
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      data: json.data,
      info: json.info
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function getHistoryInstalledProducts(token, topFills, params) {
  try {
    const res = await Promise.all(
      topFills.map(topFill => {
        const result = getHistoryInstalledProduct(token, topFill.id, params);
        return result;
      })
    );
    const response = {
      data: res
    };
    return response;
  } catch (error) {
    if (error.isCustomError) {
      throw error;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function getHistoryInstalledProductExport(token, id, params) {
  try {
    const query = await fetch(
      `${installedProductsV4}/${id}/history/series/download`,
      {
        method: 'get',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          Authorization: `Bearer ${token}`
        },
        params
      }
    );
    const transactionId = query.headers.get('transactionId');
    const response = await query.blob();

    if (query.status === 404) {
      throw new CustomError({ ...UNEXPECTED_ERROR, transactionId });
    }
    return response;
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function getLocationsById(token, siteId) {
  try {
    const query = await fetch(`${installedProductsV4}/locations/${siteId}`, {
      method: 'get',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: `Bearer ${token}`
      }
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      data: json.data,
      info: json.info,
      meta: json._meta
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function getHistoricalSummary({ token, turbineId, params }) {
  const url = `${installedProductsV4}/summary/${turbineId}/history/series`;
  try {
    const query = await fetch(url, {
      method: 'get',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: `Bearer ${token}`
      },
      params
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();

    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      data: json.data
    };
  } catch (err) {
    S;
    if (err.isCustomError) {
      throw err;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function getHistoricalDataOptions({
  token,
  turbineType,
  turbineSerialNumber
}) {
  try {
    const model = 'SMT60';
    const query = await fetch(
      `${installedProductsV4}/turbines/history/controls?model=${model}&qSerialNumber=${turbineSerialNumber}`,
      {
        method: 'get',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          Authorization: `Bearer ${token}`
        }
      }
    );

    const transactionId = query.headers.get('transactionId');
    const json = await query.json();

    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      data: json.data
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function getHistoryTags({ token, turbineSerialNumber }) {
  try {
    const query = await fetch(
      `${installedProductsV4}/turbines/tags?serialNumber=${turbineSerialNumber}`,
      {
        method: 'get',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          Authorization: `Bearer ${token}`
        }
      }
    );

    const transactionId = query.headers.get('transactionId');
    const json = await query.json();

    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      data: json.data
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function exportSummaryHistory({ token, params, body }) {
  try {
    const query = await fetch(
      `${installedProductsV4}/turbines/history/series/download`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          Authorization: `Bearer ${token}`
        },
        params,
        body: JSON.stringify(body?.metrics)
      }
    );

    const transactionId = query.headers.get('transactionId');

    if (!query.ok) {
      const json = await query.json();
      throw new CustomError({ ...json, transactionId });
    }

    const data = await query.text();
    return { data };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function getAlarms({ token }) {
  try {
    const query = await fetch(
      `${installedProductsV4}/turbines/history/controls`,
      {
        method: 'get',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          Authorization: `Bearer ${token}`
        }
      }
    );

    const transactionId = query.headers.get('transactionId');
    const json = await query.json();

    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      data: json.data
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function putAlarmsAcknowledge({ token, turbineId, params }) {
  try {
    const query = await fetch(
      `${installedProductsV4}/${turbineId}/alarms/ack`,
      {
        method: 'put',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(params)
      }
    );

    const transactionId = query.headers.get('transactionId');

    // Handle response - may be empty or have JSON
    let json = {};
    const contentType = query.headers.get('content-type');
    const hasJsonContent =
      contentType && contentType.includes('application/json');

    if (hasJsonContent) {
      try {
        const text = await query.text();
        json = text ? JSON.parse(text) : {};
      } catch (parseError) {
        // If JSON parsing fails, treat as empty response
        json = {};
      }
    }

    if (!query.ok) {
      throw new CustomError({
        ...(json.error || json),
        transactionId,
        code: query.status || json.error?.code,
        message:
          json.error?.message || json.message || 'Failed to acknowledge alarm'
      });
    }

    if (json.error) {
      throw new CustomError({
        ...json.error,
        transactionId,
        code: json.error?.code,
        message:
          json.error?.message || json.message || 'Failed to acknowledge alarm'
      });
    }

    return {
      data: json
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
    // Convert non-CustomError to CustomError
    throw new CustomError({
      code: err.status || 500,
      message: err.message || 'Unexpected error occurred',
      technicalMessage: err.message
    });
  }
}

export async function putAlarmsUnacknowledge({ token, turbineId, params }) {
  try {
    const query = await fetch(
      `${installedProductsV4}/${turbineId}/alarms/unack?tag=${params.tag}`,
      {
        method: 'put',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          Authorization: `Bearer ${token}`
        }
      }
    );

    const transactionId = query.headers.get('transactionId');
    const json = await query.json();

    if (!query.ok || json.error) {
      throw new CustomError({
        ...(json.error || json),
        transactionId,
        code: query.status || json.error?.code,
        message:
          json.error?.message || json.message || 'Failed to unacknowledge alarm'
      });
    }
    return {
      data: json
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function getTurbinesHistorySeries({ token, params }) {
  try {
    const query = await fetch(
      `${installedProductsV4}/turbines/history/series`,
      {
        method: 'get',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          Authorization: `Bearer ${token}`
        },
        params
      }
    );

    const transactionId = query.headers.get('transactionId');
    const json = await query.json();

    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }

    return {
      data: json.data,
      info: json.info,
      meta: json._meta
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function patchTurbineHistorySeries({ token, params, body }) {
  try {
    const { turbineId, startDate, endDate, interval, applyDateFormat } = params;
    const query = await fetch(
      `${installedProductsV4}/turbines/history/series`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          Authorization: `Bearer ${token}`
        },
        params: { turbineId, startDate, endDate, interval, applyDateFormat },
        body: JSON.stringify(body?.metrics)
      }
    );
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      data: json.data,
      info: json.info,
      meta: json._meta,
      errors: json.errors ?? []
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function getOdometer(token, { serialNumbers, productId } = {}) {
  try {
    const params = {};
    if (serialNumbers) params.serialNumbers = serialNumbers;
    if (productId) params.productId = productId;
    const query = await fetch(`${installedProductsV4}/odometer`, {
      method: 'get',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: `Bearer ${token}`
      },
      params
    });
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return { data: json.data };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function getTurbinesHistoryAuditingReport({
  token,
  params,
  body
}) {
  try {
    const query = await fetch(
      `${installedProductsV4}/turbines/history/auditing-report`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          Authorization: `Bearer ${token}`
        },
        params,
        body: JSON.stringify(body)
      }
    );
    const transactionId = query.headers.get('transactionId');
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    return {
      data: json.data,
      meta: json._meta
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function getTurbinesHistoryAuditingReportDownload({
  token,
  params,
  body
}) {
  try {
    const query = await fetch(
      `${installedProductsV4}/turbines/history/auditing-report/download`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          Authorization: `Bearer ${token}`
        },
        params,
        body: JSON.stringify(body)
      }
    );
    const transactionId = query.headers.get('transactionId');
    if (!query.ok) {
      const json = await query.json().catch(() => ({}));
      throw new CustomError({ ...json, transactionId });
    }
    const data = await query.blob();
    return { data };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  throw new CustomError(UNEXPECTED_ERROR);
}

export default { getInstalledProducts, getHistoricalAlarm };
